import type { Node, Edge } from '@xyflow/react';
import { NODE_DETAILS, DOMAINS, DOMAIN_ORDER, type DomainId } from './archData';
import { INITIAL_EDGES } from './archData';

const GROUP_COLS = 3;
const CHILD_W = 190;
const CHILD_H = 56;
const CHILD_GAP = 16;
const GROUP_HEADER_H = 40;
const GROUP_PADDING = 16;
const COLLAPSED_W = 260;
const COLLAPSED_H = 64;
const DOMAIN_GAP = 24;
const CANVAS_X = 40;

export function getDomainComponentIds(domainId: DomainId): string[] {
  return Object.values(NODE_DETAILS)
    .filter(d => d.domain === domainId)
    .map(d => d.id);
}

/** Resolves a component id to the node actually visible in the canvas (itself, or its collapsed domain). */
function resolveVisibleId(componentId: string, collapsedDomains: Set<DomainId>): string {
  const detail = NODE_DETAILS[componentId];
  if (!detail) return componentId;
  return collapsedDomains.has(detail.domain) ? detail.domain : componentId;
}

export function buildGraph(collapsedDomains: Set<DomainId>): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  let cursorY = 20;

  for (const domainId of DOMAIN_ORDER) {
    const domain = DOMAINS[domainId];
    const componentIds = getDomainComponentIds(domainId);
    const collapsed = collapsedDomains.has(domainId);

    if (collapsed) {
      nodes.push({
        id: domainId,
        type: 'default',
        position: { x: CANVAS_X, y: cursorY },
        data: { label: `${domain.label}  (${componentIds.length})` },
        style: {
          background: domain.bg,
          border: `2px solid ${domain.border}`,
          color: domain.text,
          borderRadius: 10,
          padding: '10px 16px',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          width: COLLAPSED_W,
          height: COLLAPSED_H,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
        },
      });
      cursorY += COLLAPSED_H + DOMAIN_GAP;
    } else {
      const rows = Math.ceil(componentIds.length / GROUP_COLS);
      const groupW = GROUP_COLS * CHILD_W + (GROUP_COLS + 1) * CHILD_GAP;
      const groupH = GROUP_HEADER_H + rows * CHILD_H + (rows + 1) * CHILD_GAP;

      nodes.push({
        id: domainId,
        type: 'group',
        position: { x: CANVAS_X, y: cursorY },
        data: { label: domain.label },
        style: {
          width: groupW,
          height: groupH,
          background: `${domain.bg}26`,
          border: `2px solid ${domain.border}`,
          borderRadius: 12,
          cursor: 'pointer',
        },
      });

      // Le type 'group' de React Flow n'affiche pas data.label — en-tête texte dédié
      nodes.push({
        id: `${domainId}__label`,
        type: 'default',
        parentId: domainId,
        extent: 'parent',
        selectable: false,
        draggable: false,
        connectable: false,
        position: { x: 0, y: 0 },
        data: { label: domain.label },
        style: {
          width: groupW,
          height: GROUP_HEADER_H,
          background: 'transparent',
          border: 'none',
          color: domain.text,
          fontWeight: 700,
          fontSize: 13,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingLeft: 14,
          pointerEvents: 'none',
          cursor: 'default',
          textAlign: 'left',
        },
      });

      componentIds.forEach((componentId, index) => {
        const col = index % GROUP_COLS;
        const row = Math.floor(index / GROUP_COLS);
        const detail = NODE_DETAILS[componentId];
        nodes.push({
          id: componentId,
          type: 'default',
          parentId: domainId,
          extent: 'parent',
          position: {
            x: CHILD_GAP + col * (CHILD_W + CHILD_GAP),
            y: GROUP_HEADER_H + CHILD_GAP + row * (CHILD_H + CHILD_GAP),
          },
          data: { label: detail.label },
          style: {
            background: domain.bg,
            border: `2px solid ${domain.border}`,
            color: domain.text,
            borderRadius: 8,
            padding: '6px 10px',
            fontSize: 11,
            fontWeight: 600,
            cursor: 'pointer',
            width: CHILD_W,
            height: CHILD_H,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          },
        });
      });

      cursorY += groupH + DOMAIN_GAP;
    }
  }

  // Arêtes : repliées vers l'id du domaine, dédupliquées, auto-boucles ignorées
  const seen = new Set<string>();
  const edges: Edge[] = [];
  for (const e of INITIAL_EDGES) {
    const source = resolveVisibleId(String(e.source), collapsedDomains);
    const target = resolveVisibleId(String(e.target), collapsedDomains);
    if (source === target) continue;
    const key = `${source}->${target}`;
    if (seen.has(key)) continue;
    seen.add(key);
    edges.push({ ...e, id: key, source, target });
  }

  return { nodes, edges };
}

export { GROUP_PADDING };
